/**
 * Soft-ask for push permission. Shown after a high-intent action (first
 * tracked topic) so we never cold-prompt. If the user says yes we then
 * trigger the native permission flow via subscribePush().
 *
 * Frequency: at most once every 30 days if dismissed; never again once granted.
 */
import { toast } from "sonner";
import { pushSupported, currentPermission, subscribePush } from "@/lib/push";
import { isIOS, isPWA } from "@/lib/platform";

const KEY = "atp:push-softask:v1";
const COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000;

interface State { lastAskedAt?: number; declined?: boolean }
const read = (): State => { try { return JSON.parse(localStorage.getItem(KEY) ?? "{}"); } catch { return {}; } };
const write = (s: State) => { try { localStorage.setItem(KEY, JSON.stringify(s)); } catch {} };

export function maybeAskForPush(context: "first-track" | "first-save") {
  if (typeof window === "undefined") return;
  if (!pushSupported()) {
    // iOS pre-install: nudge install instead
    if (isIOS() && !isPWA()) return;
    return;
  }
  if (currentPermission() !== "default") return;
  const s = read();
  if (s.lastAskedAt && Date.now() - s.lastAskedAt < COOLDOWN_MS) return;

  const message =
    context === "first-track"
      ? "Want a heads-up when a new episode answers this topic?"
      : "Want push alerts for episodes you save?";

  toast(message, {
    duration: 12000,
    action: {
      label: "Yes, notify me",
      onClick: async () => {
        write({ lastAskedAt: Date.now() });
        const r = await subscribePush();
        if (r.ok) toast.success("Push notifications on!");
        else if (r.reason && r.reason !== "default") toast.error(`Couldn't enable (${r.reason})`);
      },
    },
    cancel: {
      label: "Not now",
      onClick: () => write({ lastAskedAt: Date.now(), declined: true }),
    },
  });
  write({ lastAskedAt: Date.now() });
}
